import type { FC, ReactNode } from "react";

/**
 * One block of the task modal. Siblings sit inside a `divide-y` container, so
 * the padding here is what keeps every section on the same vertical rhythm.
 */
export const TaskModalSection: FC<{ label?: string; actions?: ReactNode; children: ReactNode }> = ({
  label,
  actions,
  children,
}) => (
  <section className="flex flex-col gap-2 py-4 first:pt-0 last:pb-0">
    {label || actions ? (
      <div className="flex items-center justify-between gap-2">
        {label ? <span className="text-xs text-muted-foreground">{label}</span> : null}
        {actions}
      </div>
    ) : null}
    {children}
  </section>
);

export const TaskSummaryColumns: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 items-start gap-5 sm:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
    {children}
  </div>
);

export const TaskSummaryList: FC<{
  labelColumn?: "auto" | "fixed";
  /** Values span several lines: labels line up with each value's first line. */
  multiline?: boolean;
  children: ReactNode;
}> = ({ labelColumn = "auto", multiline = false, children }) => (
  <dl
    className={`grid gap-x-3 text-xs ${multiline ? "items-start gap-y-2" : "items-center"} ${
      labelColumn === "fixed" ? "grid-cols-[7rem_minmax(0,1fr)]" : "grid-cols-[auto_minmax(0,1fr)]"
    }`}
  >
    {children}
  </dl>
);

export const TaskSummaryRow: FC<{ label: ReactNode; action?: ReactNode; children: ReactNode }> = ({
  label,
  action,
  children,
}) => (
  <>
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="flex min-h-6 min-w-0 items-center justify-between gap-2">
      {children}
      {action}
    </dd>
  </>
);
